import { useEffect, useState } from "react";
import { Building2, CalendarCheck, Inbox, Loader2, UserCheck, Users } from "lucide-react";
import { api } from "@/lib/api";
import { AnalyticsStrip, PeriodSelect } from "@/components/admin/AnalyticsStrip";

const Card = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="glass rounded-2xl p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-[#B38B2D]">
      <Icon className="h-4 w-4" aria-hidden="true" />
      <span className="text-[10px] font-semibold uppercase tracking-[0.18em]">{label}</span>
    </div>
    <p className="mt-3 font-serif text-3xl font-semibold text-[#1A1615]">{value}</p>
    {sub && <p className="mt-1 text-xs text-[#91837C]">{sub}</p>}
  </div>
);

const SourceList = ({ title, icon: Icon, rows, testid }) => {
  const max = Math.max(1, ...(rows || []).map((r) => r.count));
  return (
    <div className="glass rounded-2xl p-5" data-testid={testid}>
      <div className="mb-3 flex items-center gap-2 text-[#B38B2D]">
        <Icon className="h-4 w-4" aria-hidden="true" />
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em]">{title}</span>
      </div>
      {(!rows || rows.length === 0) ? (
        <p className="text-xs font-light text-[#B9ADA4]">No enquiries in this period yet.</p>
      ) : (
        <div className="space-y-2.5">
          {rows.map((r, i) => (
            <div key={i} data-testid={`${testid}-row`}>
              <div className="mb-1 flex items-center justify-between gap-2 text-[11px]">
                <span className="truncate text-[#5A504B]">{r.label}</span>
                <span className="shrink-0 font-semibold text-[#1A1615]">{r.count}</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-[#EFE7DC]">
                <div className="h-full rounded-full bg-[#C9A24B]" style={{ width: `${(r.count / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ReOverviewTab = () => {
  const [s, setS] = useState(null);
  const [period, setPeriod] = useState("30d");
  const [perf, setPerf] = useState(null);
  const [sources, setSources] = useState(null);
  useEffect(() => { api.get("/admin/realestate/stats").then((r) => setS(r.data)).catch(() => setS({})); }, []);
  useEffect(() => {
    api.get(`/admin/realestate/analytics?period=${period}`).then((r) => setPerf(r.data)).catch(() => setPerf(null));
    api.get(`/admin/realestate/top-sources?period=${period}`).then((r) => setSources(r.data)).catch(() => setSources(null));
  }, [period]);
  if (s === null) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#C9A24B]" /></div>;
  return (
    <div data-testid="re-overview-tab">
      <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">Docstate — Dashboard</h2>
      <p className="mt-1 text-xs text-[#91837C]">Dubai real-estate portal at a glance</p>

      <div className="glass mt-5 overflow-hidden rounded-2xl" data-testid="re-performance">
        <div className="flex items-center justify-between gap-3 border-b border-[#E8E2D9] px-5 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#B38B2D]">Performance</p>
          <PeriodSelect value={period} onChange={setPeriod} testid="re-analytics-period" />
        </div>
        <AnalyticsStrip metrics={perf} accent="#C9A24B" testid="re-analytics-strip" />
      </div>

      {sources && (sources.pages?.length > 0 || sources.sources?.length > 0) && (
        <div className="mt-5 grid gap-4 lg:grid-cols-2" data-testid="re-top-sources">
          <SourceList title="Top enquiry pages" icon={Building2} rows={sources.pages} testid="re-top-pages" />
          <SourceList title="Top sources & campaigns" icon={Users} rows={sources.sources} testid="re-top-campaigns" />
        </div>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card icon={Inbox} label="Enquiries" value={s.enquiries_total || 0} sub={`${s.enquiries_new || 0} new`} testid="re-stat-enquiries" />
        <Card icon={Building2} label="Listings" value={s.listings_total || 0} sub={`${s.listings_published || 0} live · ${s.listings_off_plan || 0} off-plan`} testid="re-stat-listings" />
        <Card icon={UserCheck} label="Agents" value={s.agents || 0} testid="re-stat-agents" />
        <Card icon={CalendarCheck} label="Viewings" value={s.viewings_total || 0} sub={`${s.viewings_new || 0} new`} testid="re-stat-viewings" />
        <Card icon={Users} label="Brokers" value={s.brokers_total || 0} sub={`${s.brokers_pending || 0} pending`} testid="re-stat-brokers" />
        <Card icon={Building2} label="Insights" value={s.posts || 0} sub={`${s.guides || 0} area guides`} testid="re-stat-posts" />
      </div>
    </div>
  );
};
